import {
  BoxDimensions,
  ButterflyGeometry,
  DielineBounds,
  DielinePanel,
  Point,
} from '../types';

// These construction rules are intentionally fixed for the first version.
const FIXED = {
  tuckFlapRatio: 0.72,
  lidWingRatio: 0.82,
  cornerChamferRatio: 0.18,
  minimumChamfer: 4,
  dimensionOffset: 14,
};

const panel = (id: string, label: string, points: Point[]): DielinePanel => ({
  id,
  label,
  points,
});

const calculateBounds = (panels: DielinePanel[]): DielineBounds => {
  const points = panels.flatMap((item) => item.points);
  const minX = Math.min(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxX = Math.max(...points.map((point) => point.x));
  const maxY = Math.max(...points.map((point) => point.y));

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Builds a simplified butterfly / mailer-box dieline in millimetres.
 *
 * Coordinate convention:
 * - the base begins at (0, 0)
 * - positive X moves right
 * - positive Y moves down
 *
 * This is suitable for UI prototyping. A production cutting die should add
 * board-caliper compensation, crease allowance, locking-slot rules and the
 * converter's machine tolerances.
 */
export const buildButterflyGeometry = ({
  length: L,
  width: W,
  height: H,
}: BoxDimensions): ButterflyGeometry => {
  const tuck = H * FIXED.tuckFlapRatio;
  const wing = H * FIXED.lidWingRatio;
  const chamfer = Math.max(FIXED.minimumChamfer, H * FIXED.cornerChamferRatio);

  const panels: DielinePanel[] = [
    panel('base', 'Base', [
      { x: 0, y: 0 },
      { x: L, y: 0 },
      { x: L, y: W },
      { x: 0, y: W },
    ]),

    panel('left-wall', 'Left wall', [
      { x: -H, y: chamfer },
      { x: 0, y: 0 },
      { x: 0, y: W },
      { x: -H, y: W - chamfer },
    ]),

    panel('right-wall', 'Right wall', [
      { x: L, y: 0 },
      { x: L + H, y: chamfer },
      { x: L + H, y: W - chamfer },
      { x: L, y: W },
    ]),

    panel('front-wall', 'Front wall', [
      { x: 0, y: W },
      { x: L, y: W },
      { x: L, y: W + H },
      { x: 0, y: W + H },
    ]),

    panel('front-left-lock', 'Front left lock', [
      { x: -H, y: W + chamfer },
      { x: 0, y: W },
      { x: 0, y: W + H },
      { x: -H + chamfer, y: W + H },
      { x: -H, y: W + H - chamfer },
    ]),

    panel('front-right-lock', 'Front right lock', [
      { x: L, y: W },
      { x: L + H, y: W + chamfer },
      { x: L + H, y: W + H - chamfer },
      { x: L + H - chamfer, y: W + H },
      { x: L, y: W + H },
    ]),

    panel('back-wall', 'Back wall', [
      { x: 0, y: -H },
      { x: L, y: -H },
      { x: L, y: 0 },
      { x: 0, y: 0 },
    ]),

    panel('lid', 'Lid', [
      { x: 0, y: -H - W },
      { x: L, y: -H - W },
      { x: L, y: -H },
      { x: 0, y: -H },
    ]),

    panel('lid-left-wing', 'Lid left wing', [
      { x: -wing + chamfer, y: -H - W },
      { x: 0, y: -H - W },
      { x: 0, y: -H },
      { x: -wing, y: -H - chamfer },
      { x: -wing, y: -H - W + chamfer },
    ]),

    panel('lid-right-wing', 'Lid right wing', [
      { x: L, y: -H - W },
      { x: L + wing - chamfer, y: -H - W },
      { x: L + wing, y: -H - W + chamfer },
      { x: L + wing, y: -H - chamfer },
      { x: L, y: -H },
    ]),

    panel('tuck-flap', 'Tuck flap', [
      { x: chamfer, y: -H - W - tuck },
      { x: L - chamfer, y: -H - W - tuck },
      { x: L, y: -H - W - tuck + chamfer },
      { x: L, y: -H - W },
      { x: 0, y: -H - W },
      { x: 0, y: -H - W - tuck + chamfer },
    ]),
  ];

  const creaseLines = [
    // Base walls
    { id: 'base-left', start: { x: 0, y: 0 }, end: { x: 0, y: W } },
    { id: 'base-right', start: { x: L, y: 0 }, end: { x: L, y: W } },
    { id: 'base-front', start: { x: 0, y: W }, end: { x: L, y: W } },
    { id: 'base-back', start: { x: 0, y: 0 }, end: { x: L, y: 0 } },

    // Hinged lid and tuck
    { id: 'back-lid', start: { x: 0, y: -H }, end: { x: L, y: -H } },
    {
      id: 'lid-tuck',
      start: { x: 0, y: -H - W },
      end: { x: L, y: -H - W },
    },

    // Lid wings
    {
      id: 'lid-left-wing',
      start: { x: 0, y: -H - W },
      end: { x: 0, y: -H },
    },
    {
      id: 'lid-right-wing',
      start: { x: L, y: -H - W },
      end: { x: L, y: -H },
    },

    // Front locking wings
    {
      id: 'front-left-lock',
      start: { x: 0, y: W },
      end: { x: 0, y: W + H },
    },
    {
      id: 'front-right-lock',
      start: { x: L, y: W },
      end: { x: L, y: W + H },
    },
  ];

  const dimensionGuides = [
    {
      id: 'length-guide',
      label: `${L} mm`,
      orientation: 'horizontal' as const,
      start: { x: 0, y: W - FIXED.dimensionOffset },
      end: { x: L, y: W - FIXED.dimensionOffset },
    },
    {
      id: 'width-guide',
      label: `${W} mm`,
      orientation: 'vertical' as const,
      start: { x: L - FIXED.dimensionOffset, y: 0 },
      end: { x: L - FIXED.dimensionOffset, y: W },
    },
    {
      id: 'height-guide',
      label: `${H} mm`,
      orientation: 'vertical' as const,
      start: { x: L / 2, y: -H },
      end: { x: L / 2, y: 0 },
    },
  ];

  return {
    panels,
    creaseLines,
    dimensionGuides,
    bounds: calculateBounds(panels),
  };
};
